import PennyInput from "@components/Inputs/PennyInput"
import { zodResolver } from "@hookform/resolvers/zod"
import { auth, auth_messages, db } from "@utils/firebase"
import type React from "react"
import { useState } from "react"
import { useCreateUserWithEmailAndPassword, useSendEmailVerification } from "react-firebase-hooks/auth"
import { type SubmitHandler, useForm } from "react-hook-form"
import { Link } from "react-router-dom"
import { twJoin, twMerge } from "tailwind-merge"
import { z } from "zod"
import { ReactComponent as PennyLogo } from "../../assets/icons/penny_logo.svg"
import { useUpdateProfile } from "react-firebase-hooks/auth"
import { collection, doc, setDoc } from "firebase/firestore"
import { AuthError } from "firebase/auth"

type SignupProps = {}

const signupSchema = z
  .object({
    email: z.string({ required_error: "Required" }).email({ message: "Must be a valid email" }),
    password: z.string({ required_error: "Required" }),
    confirmPassword: z
      .string({ required_error: "Required" }),
    firstName: z.string({ required_error: "Required" }).min(3, { message: "Must be at least 3 characters" }).max(15, { message: "Must be at most 15 characters" }).regex(/^[A-Za-z]+$/, { message: "Only letters are allowed" }),
    lastName: z.string({ required_error: "Required" }).min(3, { message: "Must be at least 3 characters" }).max(15, { message: "Must be at most 15 characters" }).regex(/^[A-Za-z]+$/, { message: "Only letters are allowed" }),
  })
  .superRefine((data, ctx) => {
    // Password validation
    if (data.password.length < 8) {
      ctx.addIssue({
        code: "custom",
        message: "Password must be at least 8 characters",
        path: ["password"],
      })
    }
    
    if (!/[A-Z]/.test(data.password)) {
      ctx.addIssue({
        code: "custom",
        message: "Password must contain at least 1 uppercase character",
        path: ["password"],
      })
    }
    
    if (!/[a-z]/.test(data.password)) {
      ctx.addIssue({
        code: "custom",
        message: "Password must contain at least 1 lowercase character",
        path: ["password"],
      })
    }
    
    if (!/[0-9]/.test(data.password)) {
      ctx.addIssue({
        code: "custom",
        message: "Password must contain at least 1 number",
        path: ["password"],
      })
    }
    
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(data.password)) {
      ctx.addIssue({
        code: "custom",
        message: "Password must contain at least 1 special character",
        path: ["password"],
      })
    }

    // Password match validation
    if (data.password !== data.confirmPassword) {
      ctx.addIssue({
        code: "custom",
        message: "Passwords do not match",
        path: ["confirmPassword"],
      })
    }
  })
type SignupSchema = z.infer<typeof signupSchema>

interface FormValues {
  email: string
  password: string
  confirmPassword: string
  firstName: string
  lastName: string
}

const Signup: React.FC<SignupProps> = () => {
  const [createUserWithEmailAndPassword, userCreated, loadingCreated, errorCreated] =
    useCreateUserWithEmailAndPassword(auth)

  const [updateProfile, updating, updateError] = useUpdateProfile(auth)
  const [sendEmailVerification, sending, emailError] = useSendEmailVerification(auth)
  const [generalError, setGeneralError] = useState<string | null>(null)

  const {
    formState: { errors },
    register,
    handleSubmit,
  } = useForm<FormValues>({
    resolver: zodResolver(signupSchema),
  })

  const onSubmit: SubmitHandler<SignupSchema> = async (data) => {
    try {
      setGeneralError(null)
      if (!userCreated) {
        const { email, password, firstName, lastName } = data

        const userData = await createUserWithEmailAndPassword(email, password)
        const uid = userData?.user?.uid

        if (uid) {
          const firstNameUppercase = firstName.charAt(0).toUpperCase() + firstName.slice(1)
          const lastNameUppercase = lastName.charAt(0).toUpperCase() + lastName.slice(1)

          await updateProfile({
            displayName: firstNameUppercase + " " + lastNameUppercase,
          })

          // Send verification email after profile is updated
          await sendEmailVerification()

          const user_ref = doc(collection(db, "users"), uid)

          await setDoc(
            user_ref,
            {
              first_name: firstNameUppercase,
              last_name: lastNameUppercase,
              email_address: email,
            },
            { merge: true },
          )
        }
      }
    } catch (error) {
      console.error("Error during signup:", error)
      setGeneralError("Failed to create account. Please try again.")
    }
  }

  // Combine all possible errors
  const combinedError = errorCreated || updateError || emailError

  // Helper function to check if the error is an AuthError with a code property
  const getErrorCode = (error: Error | AuthError | undefined): string | undefined => {
    if (error && 'code' in error) {
      return error.code;
    }
    return undefined;
  }

  const errorCode = getErrorCode(combinedError);

  return (
    <section className="w-full grow flex items-center justify-center">
      <div className="max-w-full md:max-w-[400px] md:my-4 w-full py-10 mx-auto md:border md:border-pn_gray-200 bg-pn_paper-1 rounded-3xl">
        <div className="w-full py-4 px-6 flex items-center justify-center">
          <PennyLogo width={81} height={32} />
        </div>

        <div className="flex flex-col gap-y-4 py-4 px-6">
          <h2 className="text-center text-pn-large-title text-pn_gray-950">Create an account</h2>
          <span className="text-center text-pn-body-regular !font-normal text-pn_gray-600">
            One last step before you can start enjoying your pension
          </span>
        </div>

        {generalError && (
          <div className="px-6 mb-4">
            <div className="bg-pn_red-50 border border-pn_red-500 text-pn_red-500 p-3 rounded-lg text-pn-callout-emphasis text-center">
              {generalError}
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-y-4 py-4 px-6">
          <div className={twMerge(errors.email?.message?.length ? "flex flex-col gap-y-2" : "block")}>
            <PennyInput
              type="text"
              disabled={loadingCreated || updating || sending}
              {...register("email")}
              label="Email Address"
              id="email"
              name="email"
              placeholder="E-mail"
              error={errors.email?.message?.length}
            />
            {!!errors.email?.message && (
              <span className="text-pn-callout-emphasis text-pn_red-500">{errors.email.message}</span>
            )}
            {errorCode === "auth/email-already-in-use" && (
              <span className="text-pn-callout-emphasis text-pn_red-500">
                {auth_messages[errorCode as keyof typeof auth_messages] || "Email already in use"}
              </span>
            )}
          </div>

          <div className="flex items-start gap-x-4 justify-between">
            <div className={twMerge(errors.firstName?.message?.length ? "flex flex-col gap-y-2" : "block", "flex-1")}>
              <PennyInput
                type="text"
                disabled={loadingCreated || updating || sending}
                {...register("firstName")}
                label="First name"
                id="firstName"
                name="firstName"
                placeholder="First name"
                error={errors.firstName?.message?.length}
                maxLength={15}
              />
              {!!errors.firstName?.message && (
                <span className="text-pn-callout-emphasis text-pn_red-500">{errors.firstName.message}</span>
              )}
            </div>

            <div className={twMerge(errors.lastName?.message?.length ? "flex flex-col gap-y-2" : "block", "flex-1")}>
              <PennyInput
                type="text"
                disabled={loadingCreated || updating || sending}
                {...register("lastName")}
                label="Last name"
                id="lastName"
                name="lastName"
                placeholder="Last name"
                error={errors.lastName?.message?.length}
                maxLength={15}
              />
              {!!errors.lastName?.message && (
                <span className="text-pn-callout-emphasis text-pn_red-500">{errors.lastName.message}</span>
              )}
            </div>
          </div>

          <div className={"flex flex-col gap-y-[11px]"}>
            <PennyInput
              disabled={loadingCreated || updating || sending}
              type="password"
              {...register("password")}
              name="password"
              id="password"
              label="Password"
              placeholder="Password"
              error={errors.password?.message?.length}
              withToggleShowPassword
            />
            {!!errors.password?.message && (
              <span className="text-pn_red-500 text-pn-callout-emphasis">{errors.password?.message}</span>
            )}
          </div>

          <div className={"flex flex-col gap-y-[11px]"}>
            <PennyInput
              disabled={loadingCreated || updating || sending}
              type="password"
              {...register("confirmPassword")}
              name="confirmPassword"
              id="confirmPassword"
              label="Confirm new password"
              placeholder="Confirm new password"
              error={errors.confirmPassword?.message?.length}
              withToggleShowPassword
            />
            {errors.confirmPassword && (
              <span className={twJoin("text-pn_red-500 text-pn-callout-emphasis")}> 
                {errors.confirmPassword.message}
              </span>
            )}
          </div>

          <button disabled={loadingCreated || updating || sending} type="submit" className="btn-primary-lg">
            Create account
          </button>
        </form>

        <div className="pt-4 pb-6 px-6 flex items-center justify-center">
          <span className="text-pn-callout-regular text-center">
            Already have a Penny account?{" "}
            <Link className="text-pn-callout-link" to="/login">
              Sign in
            </Link>
          </span>
        </div>

        <div className="py-4 px-6 flex items-center justify-center">
          <span className="text-pn-callout-regular text-center">
            By proceeding, you agree to the{" "}
            <Link to="https://www.thisispenny.com/terms-of-business" className="text-pn-callout-link">
              Terms and Conditions
            </Link>{" "}
            and{" "}
            <Link to="https://www.thisispenny.com/privacy-policy" className="text-pn-callout-link">
              Privacy Policy
            </Link>
          </span>
        </div>
      </div>
    </section>
  )
}
export default Signup